<script lang="ts">
  import { showSaveTrouble, retryButtonDisabled, retryButtonLabel } from "../lib/saveIndicator";
  import { replayJournal } from "../lib/liveCorrection";
  import { replayGoalJournal } from "../lib/goalCorrection";

  // "Try now" re-runs BOTH durable replays (lap-label + ring-goal). The pill hides
  // itself once the journals drain (showSaveTrouble is journal-driven), so no
  // explicit dismiss is needed. Each replay shares its in-flight promise across
  // overlapping calls, so a repeat tap while retrying can't double-dispatch — the
  // button is disabled and relabeled "Retrying…" meanwhile so that's visible.
  function retry() {
    void replayJournal();
    void replayGoalJournal();
  }
</script>

{#if $showSaveTrouble}
  <div class="save-trouble" role="status">
    <span class="msg">A change didn't save. Keeping it and retrying.</span>
    <button type="button" onclick={retry} disabled={$retryButtonDisabled}>{$retryButtonLabel}</button>
  </div>
{/if}

<style>
  .save-trouble {
    position: fixed;
    /* Centered by auto margins, NOT `left: 50%` + translate (issue #10, build
       12 screenshot 2026-09-14): a fixed box placed with `left: 50%` is
       shrink-to-fit against the HALF viewport to its right, and that
       intrinsic width undercounted the button, so the Try-now capsule hung
       12px past the pill's right edge. With both insets at 0 the fit-content
       width sees the whole viewport and the row lays out at its real size. */
    left: 0;
    right: 0;
    margin-inline: auto;
    width: fit-content;
    /* Sit just above the floating nav pill (72px) + its offset + the home
       indicator inset, so it never hides under the nav. */
    bottom: calc(72px + var(--space-3) + env(safe-area-inset-bottom, 0px));
    z-index: 60;
    display: flex;
    align-items: center;
    gap: var(--space-3);
    max-width: min(92vw, 560px);
    padding: var(--space-2) var(--space-3);
    /* Opaque floating surface (matches the BottomNav pill's --bg-raised).
       The Dawn-Glass --bg-card is 42% opaque, so the "Your swim, as sets"
       card copy behind the pill ghosted through and collided with the pill's
       own message. A floating status chip must read as a solid surface. */
    background: var(--bg-raised);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.35);
  }
  .msg {
    font-size: 14px;
    line-height: 1.35;
    color: var(--text-primary);
    /* VP gate B1 (2026-07-09): without a floor the Try-now button squeezed the
       message into a one-word-per-line ladder at 390pt. 20ch keeps it to two
       readable lines worst-case. */
    flex: 1;
    min-width: 20ch;
  }
  .save-trouble button {
    flex: none;
    white-space: nowrap;
    padding: var(--space-1) var(--space-3);
    background: var(--accent-primary);
    color: var(--bg-canvas);
    border: 0;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .save-trouble button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
</style>